import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FiCamera, FiCheck } from "react-icons/fi";
import { UserContext } from "../context/userContext";
import PageHead from "../component/PageHead";
import { FALLBACK_AVATAR, authHeaders, fallbackTo, uploadUrl } from "../constants";

const UserProfile = () => {
  const [error, setError] = useState("");
  const [avatar, setAvatar] = useState("");
  const [preview, setPreview] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [isAvatarTouched, setIsAvatarTouched] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useContext(UserContext);
  const token = currentUser?.token;
  const { id } = useParams();

  useEffect(() => {
    if (!token) navigate("/login");
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;
    const getUser = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/users/${id}`, authHeaders(token));
        const { name, email, avatar } = response.data;
        setName(name);
        setAvatar(avatar);
        setEmail(email);
      } catch (err) {
        console.log(err);
      }
    };
    getUser();
  }, [id, token]);

  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const pickAvatar = (file) => {
    if (!file) return;
    setAvatar(file);
    setPreview(URL.createObjectURL(file));
    setIsAvatarTouched(true);
  };

  const changeAvatarHandler = async () => {
    setIsAvatarTouched(false);
    try {
      const postData = new FormData();
      postData.set("avatar", avatar);
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/users/change-avatar`,
        postData,
        authHeaders(token)
      );
      setAvatar(response?.data.avatar);
      setPreview("");
    } catch (err) {
      console.log(err);
    }
  };

  const updateUserDetails = async (e) => {
    e.preventDefault();
    try {
      const userData = new FormData();
      userData.set("name", name);
      userData.set("email", email);
      userData.set("currentPassword", currentPassword);
      userData.set("newPassword", newPassword);
      userData.set("confirmNewPassword", confirmNewPassword);
      const response = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/users/edit-user`,
        userData,
        authHeaders(token)
      );
      if (response.status === 200) navigate("/logout");
    } catch (err) {
      setError(err.response?.data?.message || "Couldn't update your details.");
    }
  };

  if (!currentUser) return null;

  return (
    <div className="container">
      <PageHead num="P" label="Profile" line1="Account" line2="settings." />

      <div className="profile">
        <aside className="card profile__side reveal">
          <div className="avatar-edit">
            <span className="avatar avatar--xl">
              <img
                src={preview || uploadUrl(avatar)}
                alt=""
                onError={fallbackTo(FALLBACK_AVATAR)}
              />
            </span>
            <label htmlFor="avatar" aria-label="Change avatar">
              <FiCamera />
            </label>
            <input
              type="file"
              id="avatar"
              accept="image/png, image/jpg, image/jpeg"
              onChange={(e) => pickAvatar(e.target.files[0])}
            />
          </div>
          <h2>{currentUser.name}</h2>
          <p>{email}</p>
          {isAvatarTouched ? (
            <button className="btn btn--accent btn--sm" onClick={changeAvatarHandler}>
              <FiCheck /> Save new avatar
            </button>
          ) : (
            <Link to={`/myposts/${currentUser.id}`} className="btn btn--sm">
              My posts
            </Link>
          )}
        </aside>

        <section className="card profile__main reveal" style={{ animationDelay: "80ms" }}>
          <h3>Personal details</h3>
          <p>Saving changes signs you out so you can log in with your new details.</p>

          <form className="form" onSubmit={updateUserDetails}>
            {error && <p className="alert">{error}</p>}
            <div className="field-row">
              <div className="field">
                <label className="field__label" htmlFor="p-name">
                  Full name
                </label>
                <input id="p-name" className="input" type="text" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="field">
                <label className="field__label" htmlFor="p-email">
                  Email
                </label>
                <input id="p-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>

            <div className="divider" />

            <div className="field">
              <label className="field__label" htmlFor="p-current">
                Current password
              </label>
              <input
                id="p-current"
                className="input"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div className="field-row">
              <div className="field">
                <label className="field__label" htmlFor="p-new">
                  New password
                </label>
                <input
                  id="p-new"
                  className="input"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <div className="field">
                <label className="field__label" htmlFor="p-confirm">
                  Confirm new password
                </label>
                <input
                  id="p-confirm"
                  className="input"
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn--primary">
                Update details
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default UserProfile;
